import React, { useState, useEffect, useCallback } from 'react';
import { X, ChevronRight } from './CustomIcons';

const STEPS = [
  { target: '[data-testid="sidebar-credits"]',   title: 'Your Credit Balance',  body: 'Everything you do earns credits — toward commissary, call time, and real savings for release.' },
  { target: '[data-testid="sidebar-streak"]',    title: 'Your Daily Streak',    body: 'Your daily streak tracks consistency. The longer your streak, the bigger your bonuses.' },
  { target: '[data-testid="learn-nav-item"]',    title: 'Start Learning',       body: 'Start here. Every course leads to a real credential that employers can verify.' },
  { target: '[data-testid="daily-challenges"]',  title: 'Daily Challenges',     body: 'Complete daily challenges for bonus credits. Three new challenges every day.' },
  { target: '[data-testid="reentry-nav-item"]',  title: 'Your Reentry Plan',    body: 'Your reentry plan starts now. Every credential and dollar saved brings you closer to your new beginning.' },
];

function getRect(selector) {
  const el = document.querySelector(selector);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return { x: r.left, y: r.top, w: r.width, h: r.height };
}

export default function OnboardingTour({ onComplete }) {
  const [step, setStep] = useState(0);
  const [rect, setRect] = useState(null);
  const [visible, setVisible] = useState(false);

  const updateRect = useCallback(() => {
    const r = getRect(STEPS[step].target);
    setRect(r);
    if (!visible) setTimeout(() => setVisible(true), 80);
  }, [step, visible]);

  useEffect(() => {
    updateRect();
    window.addEventListener('resize', updateRect);
    return () => window.removeEventListener('resize', updateRect);
  }, [updateRect]);

  const handleNext = () => {
    if (step < STEPS.length - 1) {
      setVisible(false);
      setTimeout(() => { setStep(s => s + 1); }, 200);
    } else {
      handleFinish();
    }
  };

  const handleFinish = () => {
    localStorage.setItem('aos_tour_done', '1');
    onComplete();
  };

  const current = STEPS[step];
  const PAD = 10;

  // Position tooltip below or above the highlighted element
  const tooltipStyle = () => {
    if (!rect) return { top: '50%', left: '50%', transform: 'translate(-50%,-50%)' };
    const spaceBelow = window.innerHeight - rect.y - rect.h;
    const above = spaceBelow < 180;
    return {
      position: 'fixed',
      left: Math.min(Math.max(rect.x, 16), window.innerWidth - 316),
      top: above ? rect.y - 150 - PAD : rect.y + rect.h + PAD + 10,
      width: 300,
      zIndex: 9002,
    };
  };

  return (
    <>
      {/* Dark overlay with SVG spotlight cutout */}
      <svg
        style={{ position: 'fixed', inset: 0, width: '100%', height: '100%', zIndex: 9001, pointerEvents: 'none', transition: 'opacity 0.3s ease', opacity: visible ? 1 : 0 }}
        aria-hidden="true"
      >
        <defs>
          <mask id="tour-mask">
            <rect width="100%" height="100%" fill="white" />
            {rect && (
              <rect
                x={rect.x - PAD} y={rect.y - PAD}
                width={rect.w + PAD * 2} height={rect.h + PAD * 2}
                rx="8" fill="black"
              />
            )}
          </mask>
        </defs>
        <rect width="100%" height="100%" fill="rgba(0,0,0,0.72)" mask="url(#tour-mask)" />
      </svg>

      {/* Tooltip */}
      <div
        style={{
          ...tooltipStyle(),
          background: 'var(--bg-surface)', border: '1px solid rgba(249,115,22,0.3)',
          borderRadius: '14px', padding: '18px', boxShadow: '0 20px 48px rgba(0,0,0,0.5)',
          zIndex: 9002,
          opacity: visible ? 1 : 0, transform: `translateY(${visible ? 0 : 8}px)`,
          transition: 'opacity 0.25s ease, transform 0.25s ease',
        }}
        role="dialog"
        aria-label={`Tour step ${step + 1} of ${STEPS.length}: ${current.title}`}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
          <span style={{ fontSize: '11px', color: 'var(--orange)', fontWeight: 700, fontFamily: 'Inter, sans-serif' }}>
            STEP {step + 1} OF {STEPS.length}
          </span>
          <button onClick={handleFinish} aria-label="Skip tour" style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: 0 }}>
            <X size={14} />
          </button>
        </div>
        <h3 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800, fontSize: '15px', color: 'var(--text-primary)', margin: '0 0 6px' }}>
          {current.title}
        </h3>
        <p style={{ fontSize: '13px', color: 'var(--text-muted)', fontFamily: 'Inter, sans-serif', lineHeight: 1.55, margin: '0 0 14px' }}>
          {current.body}
        </p>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <button
            onClick={handleNext}
            data-testid={step === STEPS.length - 1 ? 'tour-finish-btn' : 'tour-next-btn'}
            style={{ display: 'flex', alignItems: 'center', gap: '4px', background: '#F97316', color: '#111111', border: 'none', borderRadius: '8px', padding: '8px 16px', fontSize: '12px', fontWeight: 700, cursor: 'pointer', fontFamily: 'Plus Jakarta Sans, sans-serif' }}
          >
            {step === STEPS.length - 1 ? "Got it!" : "Next"} <ChevronRight size={12} />
          </button>
          {step < STEPS.length - 1 && (
            <button onClick={handleFinish} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: '12px', cursor: 'pointer', fontFamily: 'Inter, sans-serif' }}>
              Skip tour
            </button>
          )}
        </div>
        {/* Step dots */}
        <div style={{ display: 'flex', gap: '5px', marginTop: '12px' }}>
          {STEPS.map((_, i) => (
            <div key={i} style={{ width: i === step ? '16px' : '5px', height: '4px', borderRadius: '2px', background: i === step ? '#F97316' : '#333', transition: 'all 0.25s' }} />
          ))}
        </div>
      </div>
    </>
  );
}
